<template>
  <!-- Fills the space under an article so the right column ends level with the sidebar.
       Desktop only: on phones the page is a single column and has no gap. -->
  <section class="hidden flex-1 flex-col gap-6 md:flex">
    <!-- Woven divider + heading -->
    <div v-reveal="{ y: 20 }">
      <div class="gamosa-strip rounded-sm" aria-hidden="true"></div>
      <div class="section-heading mt-5">
        <h2 class="section-title text-2xl">আৰু পঢ়ক</h2>
        <span class="section-subtitle">Continue reading</span>
      </div>
    </div>

    <!-- Cards -->
    <div class="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      <router-link
        v-for="(item, i) in cards"
        :key="item.key"
        v-reveal="{ delay: i * 0.08 }"
        :to="item.to"
        class="card group flex flex-col overflow-hidden transition-shadow duration-300 hover:shadow-card-hover"
      >
        <div :class="['relative aspect-[4/3] overflow-hidden', item.style === 'photo' ? 'bg-gray-100' : 'flex items-center justify-center bg-gradient-to-br from-brand-cream/80 via-brand-cream/35 to-white']">
          <img :src="item.image" :alt="item.title" :class="imageClass[item.style]" />
          <span class="badge absolute left-3 top-3 bg-white/90 text-brand-red-dark shadow-sm">{{ item.tag }}</span>
        </div>
        <div class="flex flex-1 flex-col p-5">
          <h3 class="font-serif text-lg font-bold leading-snug text-gray-900 transition-colors group-hover:text-brand-red">{{ item.title }}</h3>
          <p class="mt-1 text-sm text-gray-500">{{ item.subtitle }}</p>
          <span class="link-arrow mt-auto pt-4">
            <span>{{ item.cta }}</span>
            <i class="fa-solid fa-arrow-right"></i>
          </span>
        </div>
      </router-link>
    </div>

    <!-- Closing panel: grows to fill whatever space is left -->
    <div v-reveal="{ delay: 0.15 }" class="card relative flex min-h-[180px] flex-1 flex-col items-center justify-center overflow-hidden px-8 py-10 text-center">
      <div class="gamosa-strip absolute inset-x-0 top-0" aria-hidden="true"></div>
      <div class="gamosa-strip absolute inset-x-0 bottom-0" aria-hidden="true"></div>
      <div class="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-cream/60 via-white/40 to-brand-cream/40"></div>

      <img :src="jaapi" alt="" class="relative h-16 w-16 drop-shadow-md" />
      <p class="relative mt-4 font-serif text-2xl font-bold text-brand-red">প্ৰতি মাহে নতুন সংখ্যা</p>
      <p class="relative mt-1 max-w-md text-sm text-gray-600">
        A new issue of the cultural newspaper and magazine every month.
      </p>
      <div class="relative mt-5 flex flex-wrap justify-center gap-3">
        <router-link to="/" class="btn-primary">
          <i class="fa-regular fa-newspaper text-xs"></i>
          Browse issues
        </router-link>
        <router-link to="/subscribe" class="btn-outline">Subscribe</router-link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import jaapi from '@/assets/images/japi2.png'
import { API_BASE_URL } from '@/api'

// 'vision' on the Vision page, or the editorial id being read
const props = defineProps({
  exclude: { type: [String, Number], default: null },
})

const imageClass = {
  photo: 'absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105',
  emblem: 'h-3/5 w-3/5 object-contain drop-shadow-md transition-transform duration-700 ease-out group-hover:rotate-12 group-hover:scale-105',
  // Portrait newspaper cover shown whole, like a magazine on a stand
  cover: 'h-[86%] w-auto rounded-sm object-contain shadow-lg ring-1 ring-black/5 transition-transform duration-500 ease-out group-hover:-translate-y-1',
}

const editorials = ref([])
const latest = ref(null)

const editorialImage = (file) => {
  try {
    return require(`@/assets/images/${file}`)
  } catch {
    return jaapi
  }
}

const cards = computed(() => {
  const list = editorials.value
    .filter(e => String(e.id) !== String(props.exclude))
    .map(e => ({
      key: `editorial-${e.id}`,
      to: `/editorial/${e.id}`,
      image: editorialImage(e.image_url),
      tag: 'সম্পাদকীয়',
      title: e.title,
      subtitle: e.subtitle,
      style: 'photo',
      cta: 'Read More',
    }))

  if (props.exclude !== 'vision') {
    list.push({
      key: 'vision',
      to: '/vision',
      image: jaapi,
      style: 'emblem',
      tag: 'দৃষ্টিভংগী',
      title: 'Vision',
      subtitle: 'সংস্কৃতিৰ স্বচ্ছ দাপোণ',
      cta: 'Read More',
    })
  }

  if (latest.value) {
    list.push({
      key: 'latest',
      to: '/',
      image: latest.value.thumbnail_path,
      style: 'cover',
      tag: 'Latest issue',
      title: latest.value.title,
      subtitle: 'Cultural Newspaper Dapon',
      cta: 'Read the issue',
    })
  }

  return list.slice(0, 3)
})

onMounted(async () => {
  const [eds, papers] = await Promise.allSettled([
    axios.get(`${API_BASE_URL}/editorials`),
    axios.get(`${API_BASE_URL}/newspapers`),
  ])
  if (eds.status === 'fulfilled') editorials.value = eds.value.data
  if (papers.status === 'fulfilled' && papers.value.data.length) latest.value = papers.value.data[0]
})
</script>

<style scoped>
/* Thin woven gamosa border */
.gamosa-strip {
  height: 22px;
  background-image: url('@/assets/gamosa/border.svg');
  background-repeat: repeat-x;
  background-size: auto 100%;
  background-position: center;
}
</style>
